import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Tooltip,
  Chip,
} from '@nextui-org/react';
import { MdOutlineEdit, MdDeleteOutline } from 'react-icons/md';
import { formatPrice } from 'common/src/utils/functions';
import useGeneralStore from '../../../store/useGeneralStore';
import { formatDateTime, truncateDescription } from 'common/src/utils/functions';
import { FaEye } from 'react-icons/fa';
import { Tasks, TaskSource, TaskWithAsker } from 'common/src/utils/types';

// Add this function at the top of your component or in a separate utility file
const getPaymentChipProps = (task: Tasks & { paymentStatus?: string }) => {
  if (task.payment_method && task.payment_method.toLowerCase().includes('card')) {
    if (!task.hasPayment) {
      return {
        color: 'default' as const,
        className: 'bg-gray-200 text-gray-800',
        text: 'Card - No Pagado',
      };
    } else if (task.hasPayment && task.paymentStatus === 'approved') {
      return {
        color: 'success' as const,
        className: 'bg-green-200 text-green-800',
        text: 'Card - Pagado',
      };
    } else if (task.hasPayment && task.paymentStatus === 'rejected') {
      return {
        color: 'danger' as const,
        className: 'bg-red-200 text-red-800',
        text: 'Card - Rechazado',
      };
    } else if (task.hasPayment && task.paymentStatus === 'failed') {
      return {
        color: 'warning' as const,
        className: 'bg-orange-200 text-orange-800',
        text: 'Card - Fallido',
      };
    } else {
      return {
        color: 'secondary' as const,
        className: 'bg-purple-200 text-purple-800',
        text: 'Card - Otro',
      };
    }
  }
  return null;
};

interface TaskTableProps {
  tasks: TaskWithAsker[];
  onEdit: (task: Tasks) => void;
  onDelete: (task: Tasks) => void;
  onOpenTaskModal: (task: Tasks) => void;
}

const TaskTable: React.FC<TaskTableProps> = ({ tasks = [], onEdit, onDelete, onOpenTaskModal }) => {
  const { fetchAskers } = useGeneralStore();
  const [isLoadingAskers, setIsLoadingAskers] = useState(true);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [localTasks, setLocalTasks] = useState(tasks);

  const toggleSortOrder = useCallback(() => {
    setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
  }, []);

  const sortedTasks = useMemo(() => {
    if (!Array.isArray(localTasks)) return [];
    return [...localTasks].sort((a, b) => {
      const dateA = new Date(a?.created_at || 0).getTime();
      const dateB = new Date(b?.created_at || 0).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [localTasks, sortOrder]);

  useEffect(() => {
    setLocalTasks(Array.isArray(tasks) ? tasks : []);
  }, [tasks]);

  useEffect(() => {
    const loadAskers = async () => {
      try {
        await fetchAskers();
      } finally {
        setIsLoadingAskers(false);
      }
    };
    loadAskers();
  }, [fetchAskers]);

  if (isLoadingAskers) {
    return <div>Cargando...</div>;
  }

  const renderTaskSource = (source: TaskSource): string => {
    switch (source) {
      case 'admin':
        return 'Admin';
      case 'asker-app':
        return 'App';
      case 'asker-web':
        return 'Web';
      default:
        return 'Otro';
    }
  };

  return (
    <Table aria-label="Tasks table">
      <TableHeader>
        <TableColumn>
          <div className="flex items-center cursor-pointer" onClick={toggleSortOrder}>
            Fecha y Hora
            <span className="ml-2">{sortOrder === 'desc' ? '↓' : '↑'}</span>
          </div>
        </TableColumn>
        <TableColumn>Nombre del Solicitante</TableColumn>
        <TableColumn>Apellido</TableColumn>
        <TableColumn>Origen</TableColumn>
        <TableColumn>Precio</TableColumn>
        <TableColumn>Base (sin IVA ni Fee)</TableColumn>
        <TableColumn>Estado</TableColumn>
        <TableColumn>Urgencia</TableColumn>
        <TableColumn>Estado de Pago</TableColumn>
        <TableColumn>Descripción</TableColumn>
        <TableColumn>Acciones</TableColumn>
      </TableHeader>
      <TableBody>
        {(sortedTasks || []).map((task) => (
          <TableRow key={task?.id}>
            <TableCell>{task?.created_at ? formatDateTime(task.created_at) : 'N/A'}</TableCell>
            <TableCell>{task.asker?.name || task.name}</TableCell>
            <TableCell>{task?.asker?.last_name || task.last_name}</TableCell>
            <TableCell>{renderTaskSource(task?.source)}</TableCell>
            <TableCell>{task?.price ? formatPrice(task.price) : 'N/A'}</TableCell>
            <TableCell>{task?.base_amount ? formatPrice(task.base_amount) : 'N/A'}</TableCell>
            <TableCell>
              <Chip color="primary" variant="flat" size="sm">
                {task?.status || 'N/A'}
              </Chip>
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-2">
                {task?.is_asap && (
                  <Chip color="danger" variant="flat" size="sm">
                    ASAP!
                  </Chip>
                )}
                {task?.is_flexible_date && (
                  <Chip color="warning" variant="flat" size="sm">
                    Fecha Flexible
                  </Chip>
                )}
              </div>
            </TableCell>
            <TableCell>
              {task?.payment_method && task.payment_method.toLowerCase().includes('card') && (
                <Chip
                  color={getPaymentChipProps(task)?.color}
                  className={getPaymentChipProps(task)?.className}
                  variant="flat"
                  size="sm"
                >
                  {getPaymentChipProps(task)?.text}
                </Chip>
              )}
            </TableCell>
            <TableCell>
              <Tooltip content={task?.description}>
                <span>{task?.description ? truncateDescription(task.description, 50) : 'N/A'}</span>
              </Tooltip>
            </TableCell>
            <TableCell>
              <div className="flex space-x-2">
                <Button onPress={() => onOpenTaskModal(task)} isIconOnly className="bg-white">
                  <FaEye size={20} color="#000080" />
                </Button>
                <Button onPress={() => onEdit(task)} isIconOnly className="bg-white">
                  <MdOutlineEdit size={20} color="#000080" />
                </Button>
                <Button isIconOnly onPress={() => onDelete(task)} className="bg-white">
                  <MdDeleteOutline size={20} color="#8B0000" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default TaskTable;
